import {
  PRO_PRICE_PER_MONTH,
  PRO_REGION_NOTE,
  PRO_TRIAL_DAYS,
  PRO_TRIAL_SUMMARY,
} from "~/utils/pricing";
import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import { TurnstileField } from "~/components/TurnstileField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  Card,
  PasswordField,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { usePasswordValidation } from "~/hooks/usePasswordValidation";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { registerSchema } from "~/utils/form-schemas";
import {
  getDevAuthDefaults,
  isRegistrationEnabled,
} from "~/utils/runtime-config";
import { isTurnstileConfigured } from "~/utils/turnstile";
import {
  getCheckoutPath,
  normalizeSignupIntent,
  savePendingCheckoutIntent,
  type SignupIntent,
} from "~/utils/signup-intent";

type RegisterValues = z.infer<typeof registerSchema>;

export default function RegisterScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    plan?: string;
    billing?: string;
    callbackURL?: string;
  }>();
  const { register, registerPending } = useAuth();
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const initialIntent = useMemo(
    () => normalizeSignupIntent(params),
    [params.billing, params.plan],
  );
  const [selectedIntent, setSelectedIntent] =
    useState<SignupIntent>(initialIntent);
  const registrationEnabled = isRegistrationEnabled();
  const turnstileEnabled = isTurnstileConfigured();
  const devDefaults = getDevAuthDefaults();
  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: devDefaults.firstName,
      lastName: devDefaults.lastName,
      email: devDefaults.email,
      country: "NZ",
      password: devDefaults.password,
      confirmPassword: devDefaults.password,
      captchaToken: "",
    },
  });
  const password = form.watch("password");
  const validation = usePasswordValidation(password);
  const checkoutPath = getCheckoutPath(
    selectedIntent.billing,
    selectedIntent.plan,
  );
  const destination = normalizeAuthRedirectPath(
    params.callbackURL,
    checkoutPath,
  );
  const loginPath = `/login?callbackURL=${encodeURIComponent(destination)}`;

  useEffect(() => {
    setSelectedIntent(initialIntent);
  }, [initialIntent]);

  const selectIntent = (intent: SignupIntent) => {
    setSelectedIntent(intent);
  };

  const submit = form.handleSubmit(async (values) => {
    if (turnstileEnabled && !values.captchaToken) {
      form.setError("captchaToken", { message: "Complete the spam check." });
      return;
    }
    try {
      savePendingCheckoutIntent(selectedIntent);
      const result = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        country: values.country,
        password: values.password,
        callbackUrl: destination,
        captchaToken: turnstileEnabled
          ? values.captchaToken || undefined
          : undefined,
      });
      if (result.signedIn) {
        router.replace(destination as never);
      } else setRegistrationComplete(true);
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error ? error.message : "Registration failed.",
      });
    }
  });

  return (
    <AuthScreen title="Create account">
      {!registrationEnabled ? (
        <Card>
          <AppText>Registrations are not currently open.</AppText>
        </Card>
      ) : registrationComplete ? (
        <Card gap="sm">
          <AppText weight="semibold">Check your email</AppText>
          <AppText>
            If a confirmation email is needed, follow the link sent to your
            email, then return to sign in
            {` and start your ${PRO_TRIAL_DAYS}-day free trial of MintStint Pro, then ${PRO_PRICE_PER_MONTH}.`}
          </AppText>
          <AppLink href={loginPath as never}>Back to sign in</AppLink>
        </Card>
      ) : (
        <Stack>
          <PlanSelector value={selectedIntent} onChange={selectIntent} />
          {form.formState.errors.root?.message ? (
            <AppText tone="danger" size="xs">
              {form.formState.errors.root.message}
            </AppText>
          ) : null}
          <View style={stylesheet.nameRow}>
            <View style={stylesheet.nameCell}>
              <FormField
                control={form.control}
                name="firstName"
                label="First name"
              >
                {(field) => (
                  <TextField
                    {...field}
                    value={String(field.value ?? "")}
                    autoComplete="given-name"
                  />
                )}
              </FormField>
            </View>
            <View style={stylesheet.nameCell}>
              <FormField
                control={form.control}
                name="lastName"
                label="Last name"
              >
                {(field) => (
                  <TextField
                    {...field}
                    value={String(field.value ?? "")}
                    autoComplete="family-name"
                  />
                )}
              </FormField>
            </View>
          </View>
          <FormField control={form.control} name="email" label="Email">
            {(field) => (
              <TextField
                {...field}
                value={String(field.value ?? "")}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                placeholder="you@example.com"
              />
            )}
          </FormField>
          <FormField
            control={form.control}
            name="country"
            label="Country"
            description="MintStint is currently available in New Zealand only."
          >
            {(field) => (
              <SearchableSelect
                title="Choose country"
                value={String(field.value ?? "NZ")}
                options={[
                  { label: "New Zealand", value: "NZ" },
                  { label: "Australia", value: "AU" },
                  { label: "United Kingdom", value: "GB" },
                  { label: "United States", value: "US" },
                  { label: "Other", value: "OTHER" },
                ]}
                placeholder="Choose country"
                onValueChange={(value) => {
                  field.onChangeText(value);
                  field.onBlur();
                }}
              />
            )}
          </FormField>
          <FormField control={form.control} name="password" label="Password">
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="Password"
              />
            )}
          </FormField>
          <FormField
            control={form.control}
            name="confirmPassword"
            label="Confirm password"
          >
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="Confirm password"
              />
            )}
          </FormField>
          {validation ? (
            <Card compact>
              <AppText size="xs">
                Password strength: {validation.strength}
              </AppText>
              {validation.errors.map((message) => (
                <AppText key={message} tone="danger" size="xs">
                  - {message}
                </AppText>
              ))}
            </Card>
          ) : null}
          {turnstileEnabled ? (
            <TurnstileField
              onToken={(token) =>
                form.setValue("captchaToken", token ?? "", {
                  shouldValidate: true,
                })
              }
            />
          ) : null}
          {form.formState.errors.captchaToken?.message ? (
            <AppText tone="danger" size="xs">
              {form.formState.errors.captchaToken.message}
            </AppText>
          ) : null}
          <Button onPress={() => void submit()} disabled={registerPending}>
            <ButtonText>
              {registerPending ? "Creating account..." : "Create account"}
            </ButtonText>
          </Button>
          <AppLink href={loginPath as never}>
            Already have an account? Sign in
          </AppLink>
        </Stack>
      )}
    </AuthScreen>
  );
}

function PlanSelector({
  value,
  onChange,
}: {
  value: SignupIntent;
  onChange: (value: SignupIntent) => void;
}) {
  const options: Array<{
    label: string;
    intent: SignupIntent;
    detail: string;
  }> = [
    {
      label: "MintStint Pro",
      intent: { plan: "pro", billing: "monthly" },
      detail: `${PRO_TRIAL_SUMMARY} flat. ${PRO_REGION_NOTE}`,
    },
  ];
  return (
    <View style={stylesheet.planSelector}>
      <AppText weight="semibold">Your plan</AppText>
      <View style={stylesheet.planOptions}>
        {options.map((option) => {
          const selected =
            option.intent.plan === value.plan &&
            option.intent.billing === value.billing;
          return (
            <Button
              key={option.label}
              variant={selected ? "primary" : "outline"}
              compact
              onPress={() => onChange(option.intent)}
            >
              <ButtonText>{option.label}</ButtonText>
            </Button>
          );
        })}
      </View>
      <AppText size="xs">
        {options.find(
          (option) =>
            option.intent.plan === value.plan &&
            option.intent.billing === value.billing,
        )?.detail ?? "1 account"}
      </AppText>
    </View>
  );
}

const stylesheet = StyleSheet.create({
  nameRow: { flexDirection: "row", flexWrap: "wrap", gap: 16 },
  nameCell: { flexGrow: 1, flexBasis: 170 },
  planSelector: {
    gap: 8,
  },
  planOptions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
});
